// ============================================================
// ActionColourAccordion.tsx
// SHARED action-type colour picker used by BOTH the admin firm-scheme
// panel (AdminPage) and the per-user override panel (Settings).
//
// Pure presentation: it owns only the accordion's expand/collapse UI
// state (expandedType — one open at a time). The colour SELECTIONS and
// the pick handler are owned by the parent, so each caller decides what
// a pick means (firm scheme vs. the user's currentScheme) and how it's
// persisted. Extracted from AdminPage's TimelineColoursPanel WITHOUT
// behaviour change — the markup/classes are identical, so the admin
// panel keeps working exactly as before.
//
// Renders every ACTION_TYPE as a compact row (label + current-colour
// dot + chevron); clicking expands ONE row to reveal a live timeline-row
// preview + the full swatch picker. Colours are TOKENS (keys of PALETTE)
// — never class strings — so nothing here can introduce an unscanned
// (purged) Tailwind class.
// ============================================================
import React, { useState } from 'react'
import { ACTION_TYPES } from '../../constants/actionTypes.constants'
import { PALETTE, PALETTE_TOKENS, SHIPPED_DEFAULT_SCHEME } from '../../constants/palette.constants'
import { PresetColourPicker, type PresetSwatch } from './ColourPicker'

// The palette as preset swatches for the shared colour picker (PALETTE's
// literal swatch classes — purge-safe; the value stays the TOKEN).
const TOKEN_SWATCHES: readonly PresetSwatch[] = PALETTE_TOKENS.map((tk) => ({ value: tk, name: tk.charAt(0).toUpperCase() + tk.slice(1), className: PALETTE[tk].swatch }))

interface ActionColourAccordionProps {
  // token per action type (always a valid selection — a shipped default
  // or a validated token — so PALETTE[token] resolves).
  selections: Record<string, string>
  // called when a swatch is picked: (actionType, token).
  onPick: (actionType: string, token: string) => void
}

export const ActionColourAccordion: React.FC<ActionColourAccordionProps> = ({ selections, onPick }) => {
  // The action type whose editor is expanded (null = all collapsed).
  // ONE open at a time. Presentation state only.
  const [expandedType, setExpandedType] = useState<string | null>(null)

  // Only action types that participate in the palette scheme (i.e. have a
  // shipped-default token) are colour-configurable here. Some action types —
  // e.g. 'email', which carries its own fixed pill in ACTION_TYPES and has NO
  // shipped default (resolveActionColour returns null → the timeline uses its
  // own fallback) — must be SKIPPED: mapping them would look up PALETTE[undefined]
  // and crash on entry.swatch. This mirrors the colour system's "no shipped
  // default" contract rather than assuming every ACTION_TYPE is palette-backed.
  const colourableTypes = ACTION_TYPES.filter(({ value }) => !!SHIPPED_DEFAULT_SCHEME[value])

  return (
    <div className="border border-casevo-border rounded-lg divide-y divide-casevo-border overflow-hidden">
      {colourableTypes.map(({ value, label }) => {
        const token = selections[value]
        // token is always a valid selection so PALETTE[token] resolves;
        // the ?? is belt-and-braces so a preview never renders empty.
        const entry = PALETTE[token] ?? PALETTE[SHIPPED_DEFAULT_SCHEME[value]]
        const isOpen = expandedType === value
        const panelId = `colour-panel-${value}`
        return (
          <div key={value}>
            {/* Collapsed row header — a real <button> so Enter/Space
                toggle it natively; aria-expanded/-controls wired. */}
            <button
              type="button"
              onClick={() => setExpandedType(isOpen ? null : value)}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-casevo-mint/20 transition-colors focus:outline-none focus-visible:bg-casevo-mint/20"
            >
              <span className="text-sm text-casevo-forest font-medium">{label}</span>
              <span className="flex items-center gap-3 shrink-0">
                {/* Current-colour indicator — solid swatch dot. */}
                <span className={`w-4 h-4 rounded-full ${entry.swatch}`} aria-hidden="true" />
                {/* Chevron — rotates when expanded. */}
                <svg
                  width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  aria-hidden="true"
                  className={`text-casevo-mid transition-transform ${isOpen ? 'rotate-180' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
            {/* Expanded editor — mounted only when open (so collapsed
                swatches aren't hidden-but-focusable). */}
            {isOpen && (
              <div id={panelId} className="px-4 pb-4 pt-1">
                {/* Live row preview — identical markup to a real timeline
                    row (bg-casevo-input + border-l-[3px] stripe + type
                    pill + action text + muted time). */}
                <div className={`bg-casevo-input border border-casevo-border border-l-[3px] rounded-lg px-3 py-2 mb-4 ${entry.stripe}`}>
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded shrink-0 ${entry.pill}`}>
                      {label}
                    </span>
                    <span className="text-sm text-casevo-forest font-medium min-w-0">
                      Example activity
                    </span>
                    <span className="text-xs text-casevo-text-soft ml-auto shrink-0">
                      Just now
                    </span>
                  </div>
                </div>
                {/* Colour — the shared colour picker in preset mode (every
                    palette token; the popover lists them, selected ringed). */}
                <PresetColourPicker label={`Colour for ${label}`} value={token} swatches={TOKEN_SWATCHES} onPick={(tk) => onPick(value, tk)} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
